import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import cloudApi from '../../../services/cloudApi';
import analytics from '../../../services/analytics';
import { useAuth } from '../../../contexts/AuthContext';
import HeroSection from '../BillingView/components/HeroSection';
import StatusBanners from '../BillingView/components/StatusBanners';
import TrustBar from '../BillingView/components/TrustBar';
import CreditTopUps from '../BillingView/components/CreditTopUps';
import TestimonialsStrip from '../BillingView/components/TestimonialsStrip';
import CheckoutSuccessModal from '../BillingView/components/CheckoutSuccessModal';
import PlanGridV2 from './components/PlanGridV2';
import FAQSectionV2 from './components/FAQSectionV2';
// LEGACY LIFETIME: see utils/legacyLifetime.js. Remove with the feature — but
// note `getPlanAction` must then still gate on a real subscription, not on
// `plan !== 'free'`, for enterprise and admin-set plans.
import { hasStripeSubscription } from '../../../utils/legacyLifetime';

const NON_OWNER_TOOLTIP = 'Only the workspace owner can manage subscription options.';

const PlansViewV2 = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace, workspaces, switchWorkspace, subscription: billingStatus, refreshSubscription } = useAuth();
  const isOwner = currentWorkspace?.role === 'owner';
  const [plans, setPlans] = useState([]);
  const [creditTopUps, setCreditTopUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [notification, setNotification] = useState(null);
  const [memberBannerDismissed, setMemberBannerDismissed] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const plansData = await cloudApi.getAvailablePlans('v2_2026');
      setPlans(plansData.plans || []);
      setCreditTopUps(plansData.credit_topups || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Deep link from the OAuth consent page: /plans?ws=<id>&source=consent pins the
  // workspace being upgraded (the one picked on the consent screen), so a
  // multi-workspace user can never pay for the wrong one. The banner below names
  // it, and `source=consent` follows the checkout through Stripe so the success
  // screen can point back at the consent tab. See OAuthConsent.jsx (upgradeUrl).
  // Preview: /plans?ws=<any workspace id you belong to>&source=consent
  const wsParam = searchParams.get('ws');
  const sourceParam = searchParams.get('source');
  const wsPinnedRef = useRef(false);
  useEffect(() => {
    if (wsPinnedRef.current || !wsParam || !workspaces?.length) return;
    wsPinnedRef.current = true;
    if (wsParam !== currentWorkspace?.id && workspaces.some((w) => w.id === wsParam)) {
      switchWorkspace(wsParam);
    }
  }, [wsParam, workspaces, currentWorkspace, switchWorkspace]);

  // Scroll to hash anchor (e.g. #credit-topups) after data loads
  const location = useLocation();
  useEffect(() => {
    if (!loading && location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [loading, location.hash]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      const type = searchParams.get('type');
      const plan = searchParams.get('plan');
      const amount = searchParams.get('amount');
      // Backend-appended when the checkout started from the consent funnel.
      const checkoutSource = searchParams.get('source');
      analytics.trackCheckoutCompleted({
        plan,
        source: 'plans_v2',
        flow: 'plans_v2',
        entry: checkoutSource || undefined,
        type: type === 'credits' ? 'credits' : 'subscription',
      });
      setCheckoutSuccess(
        type === 'credits'
          ? { type: 'credits', creditAmount: amount }
          : { type: 'subscription', planName: plan, source: checkoutSource }
      );
      setTimeout(() => {
        refreshSubscription();
        loadData();
      }, 2000);
    } else if (checkout === 'cancel') {
      analytics.trackCheckoutCancelled({ flow: 'plans_v2' });
      setNotification({ type: 'info', message: 'Checkout was cancelled.' });
    }
    if (checkout) {
      searchParams.delete('checkout');
      searchParams.delete('session_id');
      searchParams.delete('type');
      searchParams.delete('plan');
      searchParams.delete('amount');
      // On cancel, keep source/ws: the backend tags the cancel URL with
      // source=consent precisely so a retry from this tab stays in the consent
      // funnel (banner + source on the next checkout). Success is terminal.
      if (checkout === 'success') {
        searchParams.delete('source');
        searchParams.delete('ws');
      }
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadData, refreshSubscription]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const currentPlan = billingStatus?.subscription?.plan?.name || 'free';
  const subscriptionStatus = billingStatus?.subscription?.status;
  const cancelAtPeriodEnd = billingStatus?.subscription?.cancel_at_period_end;

  const handleBuyCredits = async (priceId) => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading(`credits-${priceId}`);
    try {
      const { url } = await cloudApi.createCreditCheckoutSession(priceId);
      analytics.trackCheckoutStarted({ type: 'credits', priceId, source: 'plans_v2', flow: 'plans_v2' });
      window.open(url, '_blank');
    } catch (error) {
      analytics.trackCheckoutFailed({ type: 'credits', priceId, flow: 'plans_v2', reason: error.message });
      setNotification({ type: 'error', message: error.message || 'Failed to start checkout' });
      setActionLoading(null);
    }
  };

  const handleSubscribe = async (planName) => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading(planName);
    const selectedPlan = plans.find((p) => p.name === planName) || planName;
    try {
      const { url } = await cloudApi.createCheckoutSession(
        planName,
        'yearly',
        sourceParam === 'consent' ? { source: 'consent' } : {}
      );
      analytics.trackCheckoutStarted({
        plan: selectedPlan,
        interval: 'yearly',
        source: 'plans_v2',
        flow: 'plans_v2',
        entry: sourceParam || undefined,
      });
      window.open(url, '_blank');
    } catch (error) {
      analytics.trackCheckoutFailed({ plan: selectedPlan, interval: 'yearly', flow: 'plans_v2', reason: error.message });
      setNotification({ type: 'error', message: error.message || 'Failed to start checkout' });
      setActionLoading(null);
    }
  };

  const handleOpenPortal = async () => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading('portal');
    try {
      const { url } = await cloudApi.createPortalSession();
      window.open(url, '_blank');
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'Failed to open billing portal' });
      setActionLoading(null);
    }
  };

  // Match on plan.name only — never display_name (legacy `pro` and `pro_2026`
  // both display "Pro" but are different plans).
  //
  // "Switch Plan" means "change the subscription you already have", so it is
  // gated on there actually being one rather than on the plan not being free.
  // LEGACY LIFETIME holders are the case that broke that assumption: they are
  // on a paid plan with no Stripe subscription and usually no Stripe customer,
  // so sending them to the billing portal was a dead end with no way out of it
  // — the one screen where they could give us money offered them a button that
  // could not open. They take the same route a free account does: checkout.
  const getPlanAction = (plan) => {
    const canSwitch = hasStripeSubscription(billingStatus);
    const isCurrent = currentPlan === plan.name;

    if (isCurrent) return { label: 'Current Plan', disabled: true };
    const base = canSwitch
      ? { label: 'Switch Plan', action: () => handleOpenPortal() }
      : { label: `Get ${plan.display_name || plan.name}`, action: () => handleSubscribe(plan.name) };
    if (!isOwner) return { ...base, disabled: true, tooltip: NON_OWNER_TOOLTIP };
    return base;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto pb-12">
      {/* Ambient background orbs — parent card handles overflow clipping */}
      <div className="pointer-events-none absolute -top-32 -left-24 w-[400px] h-[400px] rounded-full bg-blue-500/[0.04] dark:bg-blue-500/[0.07] blur-3xl" />
      <div className="pointer-events-none absolute top-[600px] -right-32 w-[350px] h-[350px] rounded-full bg-purple-500/[0.04] dark:bg-purple-500/[0.06] blur-3xl" />

      {checkoutSuccess && (
        <CheckoutSuccessModal
          type={checkoutSuccess.type}
          plan={plans.find((p) => p.name === checkoutSuccess.planName) || null}
          creditAmount={checkoutSuccess.creditAmount}
          source={checkoutSuccess.source}
          onClose={() => setCheckoutSuccess(null)}
        />
      )}

      {/* Consent-funnel context: the user came from an OAuth consent tab to
          upgrade this specific workspace. Name it, and point them back — unless
          the deep-linked workspace isn't in this account (wrong account, or
          membership changed): then say so instead of asserting the wrong one. */}
      {sourceParam === 'consent' && !checkoutSuccess && (
        wsParam && workspaces?.length && !workspaces.some((w) => w.id === wsParam) ? (
          <div className="rounded-lg p-4 border animate-fade-in mb-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
            <p className="text-sm font-medium">
              The workspace from your link isn&rsquo;t available in this account — you&rsquo;re viewing plans for{' '}
              <span className="font-bold">{currentWorkspace?.name}</span> instead. Double-check you&rsquo;re signed in
              with the right account before upgrading.
            </p>
          </div>
        ) : (
          <div className="rounded-lg p-4 border animate-fade-in mb-4 bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800 text-cyan-800 dark:text-cyan-200">
            <p className="text-sm font-medium">
              You&rsquo;re upgrading <span className="font-bold">{currentWorkspace?.name}</span> to connect your AI app.
              When you&rsquo;re done, head back to the tab you left open — it unlocks by itself.
            </p>
          </div>
        )
      )}

      {/* Notification Banner */}
      {notification && (
        <div className={`rounded-lg p-4 border animate-fade-in mb-4 ${
          notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
          : notification.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{notification.message}</p>
            <button onClick={() => setNotification(null)} className="ml-4 text-sm opacity-60 hover:opacity-100">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {!isOwner && !memberBannerDismissed && (
        <div className="rounded-lg p-4 border animate-fade-in mb-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              You're a member of this workspace. {NON_OWNER_TOOLTIP}
            </p>
            <button
              onClick={() => setMemberBannerDismissed(true)}
              className="ml-4 text-sm opacity-60 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <StatusBanners
        subscriptionStatus={subscriptionStatus}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        billingStatus={billingStatus}
        onOpenPortal={handleOpenPortal}
        disabled={!isOwner}
        disabledTooltip={NON_OWNER_TOOLTIP}
      />

      {/* Hero */}
      <div className="animate-fade-in">
        <HeroSection subtitle="Your trusted data extraction assistant — used by thousands of professionals worldwide." />
      </div>

      {/* Plan cards */}
      <div className="animate-fade-in mt-8" style={{ animationDelay: '200ms', animationFillMode: 'backwards' }}>
        <PlanGridV2
          plans={plans}
          getPlanAction={getPlanAction}
          actionLoading={actionLoading}
        />
      </div>

      {/* Trust bar */}
      <div className="animate-fade-in mt-4" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}>
        <TrustBar />
      </div>

      {/* Testimonials */}
      <div className="animate-fade-in mt-14" style={{ animationDelay: '450ms', animationFillMode: 'backwards' }}>
        <TestimonialsStrip />
      </div>

      {/* Credit top-ups */}
      <div id="credit-topups" className="animate-fade-in mt-14" style={{ animationDelay: '550ms', animationFillMode: 'backwards' }}>
        <CreditTopUps
          creditTopUps={creditTopUps}
          currentPlan={currentPlan}
          onBuyCredits={handleBuyCredits}
          actionLoading={actionLoading}
          disabled={!isOwner}
          disabledTooltip={NON_OWNER_TOOLTIP}
        />
      </div>

      {/* FAQ */}
      <div className="animate-fade-in mt-14" style={{ animationDelay: '650ms', animationFillMode: 'backwards' }}>
        <FAQSectionV2
          openIndex={openFaqIndex}
          setOpenIndex={setOpenFaqIndex}
        />
      </div>
    </div>
  );
};

export default PlansViewV2;
